import { z } from 'zod';

import { ApiClient } from '@zercode/api-client';
import type { components } from '@zercode/api-client/generated/controlplane';
import { defaultBranding, TenantBranding } from '@zercode/theme';

import { readLandingPages } from './landing';

/**
 * Runtime validation (Zod) of the Control Plane branding response
 * `GET /api/v1/tenants/{id}/branding`. The COMPILE-TIME wire type is generated
 * from OpenAPI (`TenantBrandingDto` in @zercode/api-client/generated/controlplane,
 * never hand-written [STD-FE]); this schema mirrors that FLAT contract
 * (primaryColor / logoLightUrl / fontUrl / defaultColorScheme / …) and
 * validates the UNTRUSTED response at runtime before it drives the whole
 * visual identity of the app.
 */
export const brandingWireSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string().nullish(),
  primaryColor: z.string(),
  secondaryColor: z.string(),
  accentColor: z.string().nullish(),
  backgroundColor: z.string().nullish(),
  textColor: z.string().nullish(),
  logoLightUrl: z.string().nullish(),
  logoDarkUrl: z.string().nullish(),
  faviconUrl: z.string().nullish(),
  /**
   * BRAND-UPLOAD (mig 007): an image was UPLOADED for this slot, so it wins over the `*Url` column.
   * The server sends a flag rather than a URL because it does not know its own public origin —
   * `resolveBrandingImageUrls` below turns these into absolute URLs against the API base.
   */
  logoLightUploaded: z.boolean().optional(),
  logoDarkUploaded: z.boolean().optional(),
  faviconUploaded: z.boolean().optional(),
  fontFamily: z.string(),
  fontUrl: z.string().nullish(),
  // int16 on the wire; openapi-typescript types it as number | string
  borderRadius: z.coerce.number().optional(),
  defaultColorScheme: z.string().optional(),
  /** opaque tenant extras (JsonElement) — round-tripped untouched on PUT */
  extras: z.unknown().optional(),
  updatedAt: z.string().optional()
});

export type BrandingWire = z.infer<typeof brandingWireSchema>;

/** wire DTO for `PUT /api/v1/tenants/{tenantId}/branding` — generated, not hand-written [STD-FE] */
export type UpdateTenantBrandingRequest = components['schemas']['UpdateTenantBrandingRequest'];

function normalizeColorScheme(value: string | undefined): 'light' | 'dark' | undefined {
  const scheme = value?.toLowerCase();
  return scheme === 'light' || scheme === 'dark' ? scheme : undefined;
}

/** map the wire DTO onto the UI branding model, filling gaps from the neutral defaults */
export function mapBranding(wire: BrandingWire): TenantBranding {
  return {
    tenantId: wire.tenantId,
    tenantName: wire.tenantName ?? undefined,
    colors: {
      primaryMain: wire.primaryColor,
      secondaryMain: wire.secondaryColor ?? defaultBranding.colors.secondaryMain
    },
    logo: {
      light: wire.logoLightUrl ?? undefined,
      dark: wire.logoDarkUrl ?? undefined,
      favicon: wire.faviconUrl ?? undefined,
      altText: wire.tenantName ?? undefined
    },
    typography: {
      fontFamily: wire.fontFamily || defaultBranding.typography.fontFamily,
      fontUrl: wire.fontUrl ?? undefined
    },
    borderRadius: wire.borderRadius ?? defaultBranding.borderRadius,
    defaultColorScheme: normalizeColorScheme(wire.defaultColorScheme),
    // C-7: customCss travels in the opaque `extras` jsonb (no schema change). Only a string
    // value is carried through; the theme sanitizes it before injection.
    customCss: readCustomCss(wire.extras),
    // LAND-CFG: same bag, same reason — available before first paint, so the router can use it.
    landingPages: readLandingPages(wire.extras),
    // Same opaque bag, same reasoning as customCss above.
    appTitle: readExtraString(wire.extras, 'appTitle')
  };
}

/**
 * Extract a named string from the opaque extras bag (missing, blank or non-string → undefined).
 *
 * ⚠ BLANK COUNTS AS ABSENT, and that is what makes clearing a field work. The console drops a key it
 * saves as empty, but a bag written by an older build — or by hand — can hold `""`, and a blank title
 * applied to `document.title` would leave every tab with no name at all.
 */
function readExtraString(extras: unknown, key: string): string | undefined {
  if (extras && typeof extras === 'object' && key in extras) {
    const value = (extras as Record<string, unknown>)[key];
    return typeof value === 'string' && value.trim() !== '' ? value : undefined;
  }
  return undefined;
}

/** Extract a string `customCss` from the opaque extras bag (any non-string/missing → undefined). */
function readCustomCss(extras: unknown): string | undefined {
  if (extras && typeof extras === 'object' && 'customCss' in extras) {
    const value = (extras as Record<string, unknown>).customCss;
    return typeof value === 'string' ? value : undefined;
  }
  return undefined;
}

/**
 * Fetch + validate the RAW branding wire DTO. The settings screen needs the
 * untouched wire shape (tenantId GUID for the PUT, round-tripped fields like
 * accentColor/extras that the theme model does not carry).
 */
export async function fetchTenantBrandingWire(client: ApiClient, tenantIdOrSlug: string): Promise<BrandingWire> {
  const { data } = await client.get<unknown>(`/api/v1/tenants/${encodeURIComponent(tenantIdOrSlug)}/branding`);
  // RAW on purpose — see resolveBrandingImageUrls. The settings screen round-trips logoLightUrl into
  // its PUT, so handing it a resolved URL here would persist OUR OWN image endpoint into the
  // external-URL column and quietly destroy the tenant's real CDN value.
  return brandingWireSchema.parse(data);
}

/**
 * BRAND-UPLOAD (mig 007) — turn the `*Uploaded` flags into absolute image URLs.
 *
 * An UPLOADED image beats the `*Url` column: the column is kept only so tenants who already pasted
 * an external CDN URL keep rendering (expansive migration), and uploading is the newer, explicit
 * intent. The column is left untouched so removing the upload falls back to it.
 *
 * WHY THE CLIENT BUILDS THE URL: the branding endpoint is anonymous and ControlPlane has no
 * PublicBaseUrl setting, so it cannot emit an absolute URL, and a relative one would resolve
 * against the SPA origin instead of the API. `client.baseUrl` is the same origin the branding
 * request just used, so it is correct by construction.
 *
 * `?v=updatedAt` busts the browser cache: the storage key is STABLE per slot (re-upload overwrites
 * it), so without a version marker a replaced logo would keep showing the old bytes.
 *
 * Exported for the settings screen, which re-derives previews after an upload without re-fetching.
 */
export function resolveBrandingImageUrls(wire: BrandingWire, apiBaseUrl: string): BrandingWire {
  const tenantId = encodeURIComponent(wire.tenantId);
  const base = apiBaseUrl.replace(/\/$/, '');
  const version = wire.updatedAt ? `?v=${encodeURIComponent(wire.updatedAt)}` : '';
  const url = (slot: string) => `${base}/api/v1/tenants/${tenantId}/branding/images/${slot}${version}`;

  return {
    ...wire,
    logoLightUrl: wire.logoLightUploaded ? url('logo_light') : wire.logoLightUrl,
    logoDarkUrl: wire.logoDarkUploaded ? url('logo_dark') : wire.logoDarkUrl,
    faviconUrl: wire.faviconUploaded ? url('favicon') : wire.faviconUrl
  };
}

/**
 * Fetch + validate + map tenant branding from the Control Plane.
 * Used both BEFORE first paint (main.tsx, via resolveBrandingBeforePaint)
 * and by the useTenantBranding() TanStack Query hook.
 */
export async function fetchTenantBranding(client: ApiClient, tenantId: string): Promise<TenantBranding> {
  // Resolution belongs HERE, not in the wire fetch: this is the consumer that wants a renderable
  // URL. The settings screen deliberately reads the raw wire instead (see fetchTenantBrandingWire).
  const wire = resolveBrandingImageUrls(await fetchTenantBrandingWire(client, tenantId), client.baseUrl);
  return mapBranding(wire);
}
